import React from 'react'
import { inject, observer } from 'mobx-react'
import {
  ColourPicker,
  ColourSwatch,
  ConfirmDialog,
  DeleteIcon,
  PencilIcon
} from './ManageModalControls.jsx'

const ManageTagsContent = ({ manageModalStore }) => {
  const tagsStore = manageModalStore.tags
  const {
    items, filteredItems, loading, saving, error, newName, newColour, filter,
    editingId, picker, colourChange, deleteItem
  } = tagsStore
  const [hoveredId, setHoveredId] = React.useState(null)
  const inputRef = React.useRef(null)

  React.useEffect(() => {
    inputRef.current && inputRef.current.focus()
  }, [])

  React.useEffect(() => {
    if (!editingId || !inputRef.current) return
    inputRef.current.focus()
    inputRef.current.select()
  }, [editingId])

  React.useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key !== 'Escape') return
      if (deleteItem || colourChange || picker) {
        event.stopImmediatePropagation()
        tagsStore.closeOverlays()
      } else if (editingId) {
        event.stopImmediatePropagation()
        tagsStore.cancelEdit()
      }
    }
    document.addEventListener('keydown', onKeyDown, true)
    return () => document.removeEventListener('keydown', onKeyDown, true)
  }, [tagsStore, deleteItem, colourChange, picker, editingId])

  React.useEffect(() => {
    if (picker == null) return undefined
    const onMouseDown = (event) => {
      if (event.target.closest('.manage-modal__colour-picker, .manage-modal__tag, .manage-modal__status-colour')) return
      tagsStore.setPicker(null)
    }
    document.addEventListener('mousedown', onMouseDown)
    return () => document.removeEventListener('mousedown', onMouseDown)
  }, [tagsStore, picker])

  const handleSubmit = async (event) => {
    event.preventDefault()
    const saved = await tagsStore.submit()
    if (saved && inputRef.current) inputRef.current.focus()
  }

  return (
    <div className='manage-modal__body manage-modal__body--tags'>
      <form className='manage-modal__tag-form' onSubmit={handleSubmit}>
        <div className='manage-modal__tag-input-wrap'>
          <div className='manage-modal__colour-wrap'>
            <ColourSwatch
              color={newColour}
              onClick={() => tagsStore.togglePicker('add')}
              ariaLabel='Choose new tag colour'
            />
            {picker === 'add' && (
              <ColourPicker
                selected={newColour}
                onSelect={colour => tagsStore.selectColour(colour)}
                title='Choose tag colour'
                note={null}
              />
            )}
          </div>
          <input
            className='glass'
            ref={inputRef}
            type='text'
            value={newName}
            onChange={event => tagsStore.setNewName(event.target.value)}
            placeholder='New tag'
            aria-label={editingId ? 'Edit tag' : 'New tag'}
            maxLength={255}
          />
        </div>
        <button type='submit' disabled={saving || !newName.trim()}>
          {editingId ? 'Save tag' : 'Add tag'}
        </button>
      </form>

      <input
        className='manage-modal__tag-filter glass'
        type='text'
        value={filter}
        onChange={event => tagsStore.setFilter(event.target.value)}
        placeholder='Filter tags...'
        aria-label='Filter tags'
      />

      {error && <p className='manage-modal__error'>{error}</p>}

      {loading
        ? (
          <p className='manage-modal__hint'>Loading tags...</p>
          )
        : (
          <ul className='manage-modal__list manage-modal__list--tags'>
            {filteredItems.length === 0 && (
            <li className='manage-modal__hint manage-modal__tag-empty'>
              {items.length === 0 ? 'No tags yet.' : 'No matching tags.'}
            </li>
            )}
            {filteredItems.map(item => (
              <li
                className={`manage-modal__row ${hoveredId === item.id || editingId === item.id ? 'is-hovered' : ''}`}
                key={item.id}
                onMouseEnter={() => setHoveredId(item.id)}
                onMouseLeave={() => setHoveredId(current => current === item.id ? null : current)}
              >
                <div className='manage-modal__colour-wrap'>
                  <button
                    type='button'
                    className='manage-modal__tag'
                    style={{ backgroundColor: item.color }}
                    onClick={() => tagsStore.togglePicker(item.id)}
                    aria-label={`Change ${item.name} colour`}
                  >
                    {item.name}
                  </button>
                  {picker === item.id && (
                  <ColourPicker
                    selected={item.color}
                    onSelect={colour => tagsStore.selectColour(colour)}
                    title='Choose tag colour'
                    note={null}
                  />
                  )}
                </div>
                <button
                  type='button'
                  className={`manage-modal__edit manage-modal__tag-edit ${hoveredId === item.id || editingId === item.id ? 'is-visible' : ''}`}
                  onClick={() => tagsStore.startEdit(item)}
                  aria-label={`Rename ${item.name}`}
                  tabIndex={hoveredId === item.id || editingId === item.id ? 0 : -1}
                >
                  <PencilIcon />
                </button>
                <button
                  type='button'
                  className='manage-modal__delete'
                  onClick={() => tagsStore.requestDelete(item)}
                  aria-label={`Delete ${item.name} tag`}
                >
                  <DeleteIcon />
                </button>
              </li>
            ))}
          </ul>
          )}

      {colourChange && (
        <ConfirmDialog
          title='Change tag colour?'
          confirmLabel='Change colour'
          onCancel={() => tagsStore.setColourChange(null)}
          onConfirm={() => tagsStore.confirmColourChange()}
          busy={saving}
        >
          <p>Change the colour for "{colourChange.item.name}"?</p>
        </ConfirmDialog>
      )}

      {deleteItem && (
        <ConfirmDialog
          title='Delete tag?'
          confirmLabel='Delete tag'
          confirmKind='danger'
          onCancel={() => tagsStore.cancelDelete()}
          onConfirm={() => tagsStore.confirmDelete()}
          busy={saving}
        >
          <p>Delete "{deleteItem.name}"?</p>
        </ConfirmDialog>
      )}
    </div>
  )
}

export default inject('manageModalStore')(observer(ManageTagsContent))
